import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  IconButton,
  Box,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import {
  CheckCircle,
  X,
  GraduationCap,
  Briefcase,
  IdCard,
  FileText,
  ChevronDown,
  Eye,
  Download,
  File,
} from "lucide-react";
import Button from "../common/Button";
import { formatDateForDisplay } from "../../utils/helpers";

const ProfessionalVerificationModal = ({
  open,
  onClose,
  onSubmit,
  initialData = null,
  professionalData = null,
  isLoading = false,
  title = "Verify Professional",
  submitButtonText = "Verify Professional",
}) => {
  const [formData, setFormData] = useState({
    is_verified: false,
    education_verified: false,
    employment_verified: false,
    identity_verified: false,
    licenses_verified: false,
    notes: "",
  });

  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        is_verified: initialData.is_verified || false,
        education_verified: initialData.education_verified || false,
        employment_verified: initialData.employment_verified || false,
        identity_verified: initialData.identity_verified || false,
        licenses_verified: initialData.licenses_verified || false,
        notes: initialData.notes || "",
      });
    } else {
      setFormData({
        is_verified: false,
        education_verified: false,
        employment_verified: false,
        identity_verified: false,
        licenses_verified: false,
        notes: "",
      });
    }
    setErrors({});
    setExpandedAccordion(null);
  }, [initialData, open]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const handleCheckboxChange = (field) => (event) => {
    const value = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-check the main verification if all specific verifications are checked
    if (field !== "is_verified") {
      const allVerified =
        (field === "education_verified"
          ? value
          : formData.education_verified) &&
        (field === "employment_verified"
          ? value
          : formData.employment_verified) &&
        (field === "identity_verified" ? value : formData.identity_verified) &&
        (field === "licenses_verified" ? value : formData.licenses_verified);

      setFormData((prev) => ({
        ...prev,
        is_verified: allVerified,
      }));
    }
  };

  const handleNotesChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      notes: event.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate that at least one verification is checked
    if (
      !formData.education_verified &&
      !formData.employment_verified &&
      !formData.identity_verified &&
      !formData.licenses_verified
    ) {
      setErrors({ general: "Please verify at least one category" });
      return;
    }

    setErrors({});
    onSubmit(formData);
  };

  const handleClose = () => {
    setErrors({});
    setExpandedAccordion(null);
    onClose();
  };

  // Map document categories to verification fields
  const getDocumentsForCategory = (categoryField) => {
    if (!professionalData?.documents) return [];

    // const categoryMap = {
    //   education_verified: ["education_certificates", "academic_transcripts"],
    //   employment_verified: ["cv", "work_experience"],
    //   identity_verified: ["id_document", "passport", "drivers_license"],
    //   licenses_verified: [
    //     "aviation_licenses",
    //     "professional_certifications",
    //     "medical_certificate",
    //   ],
    // };
    const categoryMap = {
      identity_verified: ["passport"],

      licenses_verified: ["aviation_licenses", "medical_certificate"],

      employment_verified: ["cv", "reference_letters", "logbook"],

      education_verified: ["educational_documents", "certificate_upload", "certificate_upload "],

      // Optional: 'other' can be excluded or used as supplemental verification
    };
    const categories = categoryMap[categoryField] || [];
    return professionalData.documents.filter((doc) =>
      categories.includes(doc.category),
    );
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(extension)) return FileText;
    if (["jpg", "jpeg", "png", "gif", "bmp"].includes(extension)) return Eye;
    return File;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      verified: "success",
      pending: "warning",
      rejected: "error",
      needs_resubmission: "info",
    };
    return statusColors[status] || "default";
  };

  const verificationItems = [
    {
      field: "education_verified",
      label: "Education Verified",
      description: "Verify educational qualifications and certificates",
      icon: GraduationCap,
      color: "blue",
      documentCategories: ["education_certificates", "academic_transcripts"],
    },
    {
      field: "employment_verified",
      label: "Employment Verified",
      description: "Verify employment history and experience",
      icon: Briefcase,
      color: "green",
      documentCategories: ["cv"],
    },
    {
      field: "identity_verified",
      label: "Identity Verified",
      description: "Verify government-issued identification",
      icon: IdCard,
      color: "purple",
      documentCategories: ["passport"],
    },
    {
      field: "licenses_verified",
      label: "Licenses Verified",
      description: "Verify professional licenses and certifications",
      icon: FileText,
      color: "orange",
      documentCategories: ["aviation_licenses", "medical_certificate"],
    },
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: "bg-blue-50 border-blue-200 text-blue-700",
      green: "bg-green-50 border-green-200 text-green-700",
      purple: "bg-purple-50 border-purple-200 text-purple-700",
      orange: "bg-orange-50 border-orange-200 text-orange-700",
    };
    return colorMap[color] || colorMap.blue;
  };

  const hasDocuments =
    professionalData?.documents && professionalData.documents.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <form onSubmit={handleSubmit} className="sentry-mask">
        <DialogTitle className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <Typography variant="h6" className="text-secondary-900">
                {title}
              </Typography>
            </div>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "gray" }}
            >
              <X />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className="pt-6 mt-2">
          {errors.general && (
            <Alert severity="error" className="mb-4">
              {errors.general}
            </Alert>
          )}

          {/* Document Summary Alert */}
          {hasDocuments && (
            <Alert severity="info" className="mb-4">
              <div className="flex items-center justify-between w-full">
                <div>
                  <Typography variant="body2" className="font-medium">
                    Document Summary
                  </Typography>
                  <Typography variant="body2">
                    {professionalData.document_summary?.total_documents || 0}{" "}
                    total documents •
                    {professionalData.document_summary?.pending_documents || 0}{" "}
                    pending •
                    {professionalData.document_summary?.verified_documents || 0}{" "}
                    verified
                  </Typography>
                </div>
              </div>
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Overall Verification Status */}
            <Grid size={{ xs: 12 }}>
              <div className="p-4 bg-gray-50 rounded-lg border mt-2">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_verified}
                      onChange={handleCheckboxChange("is_verified")}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography className="font-semibold text-lg">
                        Overall Professional Verification
                      </Typography>
                      <Typography variant="body2" className="text-gray-600">
                        Mark this professional as fully verified
                      </Typography>
                    </Box>
                  }
                />
              </div>
            </Grid>

            {/* Individual Verification Categories with Document Accordions */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle1"
                className="font-semibold mb-3 text-secondary-900"
              >
                Verification Categories & Documents
              </Typography>
              <div className="space-y-3">
                {verificationItems.map((item) => {
                  const categoryDocuments = getDocumentsForCategory(item.field);
                  const hasCategoryDocuments = categoryDocuments.length > 0;

                  return (
                    <Accordion
                      key={item.field}
                      expanded={expandedAccordion === item.field}
                      onChange={handleAccordionChange(item.field)}
                      className={`border rounded-lg transition-all ${
                        formData[item.field]
                          ? getColorClasses(item.color)
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <AccordionSummary
                        expandIcon={<ChevronDown />}
                        className="hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between w-full pr-4">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData[item.field]}
                                onChange={handleCheckboxChange(item.field)}
                                color="primary"
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                            label={
                              <Box className="flex items-center space-x-3">
                                <div
                                  className={`p-2 rounded-lg ${
                                    formData[item.field]
                                      ? "bg-white"
                                      : "bg-gray-100"
                                  }`}
                                >
                                  <item.icon
                                    className={`w-4 h-4 ${
                                      formData[item.field]
                                        ? `text-${item.color}-600`
                                        : "text-gray-400"
                                    }`}
                                  />
                                </div>
                                <Box>
                                  <Typography className="font-medium">
                                    {item.label}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    className="text-gray-600"
                                  >
                                    {item.description}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />

                          {/* Document Count Badge */}
                          {hasCategoryDocuments && (
                            <Chip
                              label={`${categoryDocuments.length} doc${
                                categoryDocuments.length > 1 ? "s" : ""
                              }`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </div>
                      </AccordionSummary>

                      <AccordionDetails className="bg-white border-t">
                        {hasCategoryDocuments ? (
                          <div className="space-y-3">
                            <Typography
                              variant="subtitle2"
                              className="font-medium text-gray-700"
                            >
                              Uploaded Documents:
                            </Typography>
                            {categoryDocuments.map((doc) => {
                              const FileIcon = getFileIcon(doc.file_name);
                              const full_url =
                                import.meta.env.VITE_BACKEND_DOMAIN +
                                doc.file_url;

                              return (
                                <Card
                                  key={doc.id}
                                  variant="outlined"
                                  className="hover:bg-gray-50"
                                >
                                  <CardContent className="p-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3 flex-1">
                                        <FileIcon className="w-5 h-5 text-gray-500" />
                                        <div className="flex-1 min-w-0">
                                          <Typography
                                            variant="body2"
                                            className="font-medium truncate"
                                          >
                                            {doc.category_display}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            className="text-gray-500"
                                          >
                                            {formatDateForDisplay(
                                              doc.uploaded_at,
                                            )}
                                          </Typography>
                                        </div>
                                      </div>

                                      <div className="flex items-center space-x-2">
                                        <Chip
                                          label={
                                            doc.verification_status_display
                                          }
                                          size="small"
                                          color={getStatusColor(
                                            doc.verification_status,
                                          )}
                                        />

                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            window.open(full_url, "_blank")
                                          }
                                          title="Preview Document"
                                        >
                                          <Eye className="w-4 h-4" />
                                        </IconButton>

                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const link =
                                              document.createElement("a");
                                            link.href = doc.file_url;
                                            link.download = doc.file_name;
                                            link.click();
                                          }}
                                          title="Download Document"
                                        >
                                          <Download className="w-4 h-4" />
                                        </IconButton>
                                      </div>
                                    </div>

                                    {/* Verification History */}
                                    {doc.latest_verification
                                      ?.verification_notes && (
                                      <Typography
                                        variant="caption"
                                        className="text-gray-600 mt-2 block"
                                      >
                                        <strong>Notes:</strong>{" "}
                                        {
                                          doc.latest_verification
                                            .verification_notes
                                        }
                                      </Typography>
                                    )}
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        ) : (
                          <Typography
                            variant="body2"
                            className="text-gray-500 text-center py-4"
                          >
                            No documents uploaded for this category
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </div>
            </Grid>

            {/* Notes */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle1"
                className="font-semibold mb-3 text-secondary-900"
              >
                Verification Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={formData.notes}
                onChange={handleNotesChange}
                placeholder="Add any notes about the verification process, document references, or additional comments..."
                variant="outlined"
              />
            </Grid>

            {/* Current Status Display */}
            {initialData && (
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="subtitle1"
                  className="font-semibold mb-3 text-secondary-900"
                >
                  Current Verification Status
                </Typography>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {verificationItems.map((item) => (
                    <div
                      key={item.field}
                      className="flex items-center space-x-2"
                    >
                      {initialData[item.field] ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <X className="w-4 h-4 text-red-500" />
                      )}
                      <span
                        className={
                          initialData[item.field]
                            ? "text-green-700"
                            : "text-red-700"
                        }
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
                {initialData.notes && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <Typography variant="body2" className="text-gray-700">
                      <strong>Previous Notes:</strong> {initialData.notes}
                    </Typography>
                  </div>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions className="border-t p-4">
          <div className="flex space-x-3 w-full justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              {submitButtonText}
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProfessionalVerificationModal;
